'use client';

import { useTranslations } from 'next-intl';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useCartStore, useShallow } from 'components/shared/Cart';
import {
  getDeviceType,
  pushToDataLayer,
} from 'components/shared/UserAnalytics';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { analytics, AnalyticsEventProps } from 'lib/analytics/analytics';

import {
  getCaRetailers,
  CaRetailer,
} from 'components/ring-air/caRetailAvailabilityConfig';
import styles from './CaRetailAvailabilityModal.module.css';

export const CA_RETAIL_AVAILABILITY_SESSION_KEY =
  'ca_retail_availability_aug26_seen';

const hasCartItems = (cart: Record<string, any> | null): boolean =>
  (cart?.lines?.edges?.length ?? 0) > 0;

const hasSeenModal = (): boolean => {
  try {
    return (
      window.sessionStorage.getItem(CA_RETAIL_AVAILABILITY_SESSION_KEY) !== null
    );
  } catch {
    return true;
  }
};

const markModalSeen = (): boolean => {
  try {
    window.sessionStorage.setItem(CA_RETAIL_AVAILABILITY_SESSION_KEY, 'shown');
    return true;
  } catch {
    return false;
  }
};

const pushRetailEvent = (event: string, data: AnalyticsEventProps): void => {
  const analyticsWindow = window as Window & {
    dataLayer?: Record<string, unknown>[];
  };
  analyticsWindow.dataLayer ??= [];
  pushToDataLayer(event, data);
  analytics.track(event, data);
};

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hasAttribute('hidden'));

const RetailerLink = ({
  retailer,
  onClick,
}: {
  retailer: CaRetailer;
  onClick: (retailer: CaRetailer) => void;
}) => {
  const t = useTranslations('ring');
  const retailerName = t(`caRetailAvailability.retailers.${retailer.id}`);

  return (
    <a
      className={styles.retailerLink}
      href={retailer.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('caRetailAvailability.retailerAriaLabel', {
        retailer: retailerName,
      })}
      onClick={() => onClick(retailer)}
    >
      {/* Retailer-approved wordmark artwork. */}
      <img
        className={
          retailer.id === 'costco'
            ? `${styles.retailerLogo} ${styles.retailerLogoCostco}`
            : styles.retailerLogo
        }
        src={retailer.logoSrc}
        alt={retailerName}
        loading="eager"
        fetchPriority="high"
      />
      <span className={styles.buyNow}>
        {t('caRetailAvailability.buyNow')} <span aria-hidden="true">→</span>
      </span>
    </a>
  );
};

export const CaRetailAvailabilityModal = () => {
  const t = useTranslations('ring');
  const internalPathname = useInternalPathname();
  const { region, language } = useRegionLocale();
  const { cart, cartHydrationState } = useCartStore(
    useShallow((s) => ({
      cart: s.cart,
      cartHydrationState: s.cartHydrationState,
    })),
  );
  const retailers = useMemo(() => getCaRetailers(language), [language]);
  const [isOpen, setIsOpen] = useState(false);
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openedAtRef = useRef(0);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const routePath =
    internalPathname.length > 1
      ? internalPathname.replace(/\/$/, '')
      : internalPathname;

  useEffect(() => {
    if (
      region?.toUpperCase() !== 'CA' ||
      routePath !== '/ring/buy' ||
      cartHydrationState !== 'ready' ||
      hasCartItems(cart) ||
      hasSeenModal()
    ) {
      return;
    }

    if (!markModalSeen()) return;
    openedAtRef.current = performance.now();
    setIsOpen(true);
    pushRetailEvent('retail_banner_impression', {
      page: window.location.pathname,
      device: getDeviceType(),
    });
  }, [cart, cartHydrationState, region, retailers, routePath]);

  useEffect(() => {
    if (isOpen && hasCartItems(cart)) setIsOpen(false);
  }, [cart, isOpen]);

  const dismiss = useCallback(() => {
    if (!isOpen) return;
    const secondsVisible = Math.max(
      0,
      Math.round((performance.now() - openedAtRef.current) / 1000),
    );
    pushRetailEvent('retail_banner_dismiss', {
      device: getDeviceType(),
      seconds_visible: secondsVisible,
    });
    setIsOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    previousActiveElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = getFocusableElements(dialogRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      previousActiveElementRef.current?.focus();
    };
  }, [dismiss, isOpen]);

  const handleRetailerClick = (retailer: CaRetailer) => {
    pushRetailEvent('retail_banner_click', {
      retailer: retailer.id,
      device: getDeviceType(),
    });
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} aria-hidden={false}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <div className={styles.header}>
          <h2 className={styles.title} id={titleId}>
            {t('caRetailAvailability.title')}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            className={styles.close}
            onClick={dismiss}
            aria-label={t('caRetailAvailability.closeAriaLabel')}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <p id={descriptionId} className={styles.description}>
          {t('caRetailAvailability.description')}
        </p>

        <div className={styles.retailers}>
          {retailers.map((retailer) => (
            <RetailerLink
              key={retailer.id}
              retailer={retailer}
              onClick={handleRetailerClick}
            />
          ))}
        </div>

        <p className={styles.finePrint}>{t('caRetailAvailability.finePrint')}</p>
      </div>
    </div>
  );
};
